import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { confirmDialog, toast } from '@kinet-open-network/client/shared/ui';
import {
  Actions,
  ROOT_EFFECTS_INIT,
  createEffect,
  ofType,
} from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  concatMap,
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';
import { AuthenticatedUser } from '../models/authenticated-user.model';
import { AbilityService } from '../services/ability.service';
import { AuthService } from '../services/auth.service';
import { AuthStorageService } from '../services/token-storage.service';
import {
  AuthApiActions,
  AuthDeviceActions,
  AuthLoginActions,
  AuthProfileActions,
  AuthShellActions,
  AuthTwoFactorLoginActions,
  PasswordResetActions,
} from './auth.actions';
import { selectAuthenticatedUser } from './auth.selectors';

export const reset$ = createEffect(
  (actions$ = inject(Actions), authService = inject(AuthService)) => {
    return actions$.pipe(
      ofType(PasswordResetActions.reset),
      concatMap(({ data }) =>
        authService.resetPassword(data).pipe(
          map(() => {
            return AuthApiActions.resetSuccess();
          }),
          catchError((error) => of(AuthApiActions.resetFailure({ error }))),
        ),
      ),
    );
  },
  {
    functional: true,
  },
);

export const forgotPwd$ = createEffect(
  (actions$ = inject(Actions), authService = inject(AuthService)) => {
    return actions$.pipe(
      ofType(AuthLoginActions.forgotPwd),
      concatMap(({ email }) =>
        authService.forgotPassword({ email }).pipe(
          map(() => {
            return AuthApiActions.forgotPwdSuccess({ email });
          }),
          catchError((error) => of(AuthApiActions.resetFailure({ error }))),
        ),
      ),
    );
  },
  {
    functional: true,
  },
);
export const forgotPwdSuccess$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) => {
    return actions$.pipe(
      ofType(AuthApiActions.forgotPwdSuccess),
      tap(() => {
        router.navigate(['/login']);
        toast({
          severity: 'success',
          summary: 'Email bol odoslaný',
        });
      }),
    );
  },
  { dispatch: false, functional: true },
);
export const resetSuccess$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) => {
    return actions$.pipe(
      ofType(AuthApiActions.resetSuccess),
      tap(() => {
        router.navigate(['/login']);
        toast({
          severity: 'success',
          summary: 'Heslo bolo úspešne zmenené',
        });
      }),
    );
  },
  { dispatch: false, functional: true },
);

export const login$ = createEffect(
  (actions$ = inject(Actions), authService = inject(AuthService)) => {
    return actions$.pipe(
      ofType(AuthLoginActions.login),
      concatMap(({ data: loginData }) =>
        authService.login(loginData).pipe(
          map(({ data }) => {
            if ('twoFactorAwaiting' in data) {
              return AuthApiActions.twoFactorAwaiting({
                data: loginData,
              });
            } else if ('twoFactorRequired' in data) {
              return AuthApiActions.twoFactorRequired({
                data: loginData,
              });
            } else {
              const { accessToken, refreshToken, user, abilities } = data;

              const authenticatedUser = AuthenticatedUser.fromResponse({
                ...user,
                abilities,
              });

              return AuthApiActions.loginSuccess({
                tokens: {
                  accessToken,
                  refreshToken,
                },
                user: authenticatedUser,
              });
            }
          }),
          catchError((error) => of(AuthApiActions.loginFailure({ error }))),
        ),
      ),
    );
  },
  {
    functional: true,
  },
);

export const twoFactorAwaiting$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) => {
    return actions$.pipe(
      ofType(AuthApiActions.twoFactorAwaiting),
      tap(({ data }) => {
        router.navigate(['/login/2fa'], {
          state: {
            twoFactorData: data,
          },
        });
      }),
    );
  },
  { dispatch: false, functional: true },
);

export const twoFactorRequired$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) => {
    return actions$.pipe(
      ofType(AuthApiActions.twoFactorRequired),
      tap(({ data }) => {
        router.navigate(['/login/2fa/required'], {
          state: {
            twoFactorData: data,
          },
        });
      }),
    );
  },
  { dispatch: false, functional: true },
);

export const twoFactorLogin$ = createEffect(
  (actions$ = inject(Actions), authService = inject(AuthService)) => {
    return actions$.pipe(
      ofType(AuthTwoFactorLoginActions.login),
      concatMap(({ data }) =>
        authService.twoFactorLogin(data).pipe(
          map(({ data }) => {
            const { accessToken, refreshToken, user, abilities } = data;

            const authenticatedUser = AuthenticatedUser.fromResponse({
              ...user,
              abilities,
            });

            return AuthApiActions.loginSuccess({
              tokens: {
                accessToken,
                refreshToken,
              },
              user: authenticatedUser,
            });
          }),
          catchError((error) => of(AuthApiActions.loginFailure({ error }))),
        ),
      ),
    );
  },
  { functional: true },
);

export const loginSuccess$ = createEffect(
  (
    actions$ = inject(Actions),
    tokenStorage = inject(AuthStorageService),
    router = inject(Router),
  ) => {
    return actions$.pipe(
      ofType(AuthApiActions.loginSuccess),
      tap(({ tokens, user }) => {
        tokenStorage.saveTokens(tokens);
        tokenStorage.saveUser(user);
        router.navigate(['/']);
        toast({
          severity: 'success',
          summary: 'Boli ste úspešne prihlásený',
        });

        if (!user.twoFactorEnabled) {
          confirmDialog({
            header: 'Dvojfaktorové overenie',
            message:
              'Nemáte aktivované dvojfaktorové overenie. Chcete ho nastaviť?',
            accept: () => {
              router.navigate(['/profile/security'], {
                queryParams: { enable2fa: true },
              });
            },
            acceptLabel: 'Áno',
            rejectLabel: 'Nie',
          });
        }
      }),
    );
  },
  { dispatch: false, functional: true },
);

export const updateAbility$ = createEffect(
  (store = inject(Store), abilityService = inject(AbilityService)) => {
    return store.select(selectAuthenticatedUser).pipe(
      startWith(null),
      distinctUntilChanged(),
      tap((user) => {
        abilityService.updateAbilities(user?.abilities ?? []);
      }),
    );
  },
  { dispatch: false, functional: true },
);

export const updateProfile$ = createEffect(
  (
    actions$ = inject(Actions),
    authService = inject(AuthService),
    store = inject(Store),
  ) => {
    return actions$.pipe(
      ofType(AuthProfileActions.update),
      concatLatestFrom(() => store.select(selectAuthenticatedUser)),
      concatMap(([{ data }, oldUser]) =>
        authService.updateProfile(data).pipe(
          map(({ data: updated }) => {
            const user = new AuthenticatedUser({
              ...oldUser,
              ...updated,
            });

            return AuthApiActions.updateProfileSuccess({ user });
          }),
          catchError((error) =>
            of(AuthApiActions.updateProfileFailure({ error })),
          ),
        ),
      ),
    );
  },
  { functional: true },
);

export const updateProfileSuccess$ = createEffect(
  (actions$ = inject(Actions), tokenStorage = inject(AuthStorageService)) => {
    return actions$.pipe(
      ofType(AuthApiActions.updateProfileSuccess),
      tap(({ user }) => {
        tokenStorage.saveUser(user);

        toast({
          severity: 'success',
          summary: 'Profil bol úspešne aktualizovaný',
        });
      }),
    );
  },
  { dispatch: false, functional: true },
);

export const logout$ = createEffect(
  (
    actions$ = inject(Actions),
    authService = inject(AuthService),
    tokenStorage = inject(AuthStorageService),
  ) => {
    return actions$.pipe(
      ofType(AuthShellActions.logout, AuthDeviceActions.logout),
      map(({ reason }) => ({
        reason,
        refreshToken: tokenStorage.getRefreshToken(),
      })),
      concatMap(({ reason, refreshToken }) => {
        if (!refreshToken) {
          return of(AuthApiActions.logoutSuccess({ reason }));
        }

        return authService.logout(refreshToken).pipe(
          map(() => AuthApiActions.logoutSuccess({ reason })),
          catchError((error) => of(AuthApiActions.logoutFailure({ error }))),
        );
      }),
    );
  },
  { functional: true },
);

export const logoutResult$ = createEffect(
  (
    actions$ = inject(Actions),
    router = inject(Router),
    tokenStorage = inject(AuthStorageService),
  ) => {
    return actions$.pipe(
      ofType(AuthApiActions.logoutSuccess, AuthApiActions.logoutFailure),
      tap((action) => {
        let msg = 'Boli ste úspešne odhlásený';

        if (action.type === AuthApiActions.logoutSuccess.type) {
          const reason = action.reason;

          switch (reason) {
            case 'USER_LOGOUT':
              msg = 'Boli ste úspešne odhlásený';
              break;
            case 'TWO_FACTOR_ENABLED':
              msg = 'Boli ste odhlásený, prihláste sa znovu';
              break;
          }
        }

        tokenStorage.clear();
        router.navigate(['/login']);

        toast({
          severity: 'info',
          summary: msg,
        });
      }),
    );
  },
  { dispatch: false, functional: true },
);

export const expired$ = createEffect(
  (
    actions$ = inject(Actions),
    authStorage = inject(AuthStorageService),
    router = inject(Router),
  ) => {
    return actions$.pipe(
      ofType(AuthDeviceActions.expired),
      tap(({ reason }) => {
        router.navigate(['/login']);

        // Handle case when this action was already dispatched to avoid multiple toasts
        // this can happen when session expires and many requests are made at the same time
        if (authStorage.getAccessToken() === null) {
          return;
        }

        authStorage.clear();

        let message = 'Boli ste odhlásený.';

        if (reason === 'TOKEN_EXPIRED') {
          message = 'Boli ste odhlásený z dôvodu neaktivity.';
        } else if (reason === 'TOKEN_REVOKED') {
          message = 'Boli ste odhlásený z bezpečnostných dôvodov.';
        }

        toast({
          severity: 'info',
          summary: message,
        });
      }),
    );
  },
  { functional: true, dispatch: false },
);

export const refreshed$ = createEffect(
  (actions$ = inject(Actions), authStorage = inject(AuthStorageService)) => {
    return actions$.pipe(
      ofType(AuthDeviceActions.refreshed),
      tap(({ tokens, user }) => {
        authStorage.saveTokens(tokens);
        authStorage.saveUser(user);
      }),
    );
  },
  { functional: true, dispatch: false },
);

export const initialize$ = createEffect(
  (
    actions$ = inject(Actions),
    tokenStorage = inject(AuthStorageService),
    authService = inject(AuthService),
  ) => {
    return actions$.pipe(
      ofType(AuthDeviceActions.init),
      switchMap(() => {
        const refreshToken = tokenStorage.getRefreshToken();

        if (refreshToken) {
          const expiration = refreshToken
            ? authService.getTokenExpiration(refreshToken)
            : 0;
          const isExpired = expiration < Date.now();

          if (isExpired) {
            return [
              AuthDeviceActions.expired({ reason: 'TOKEN_EXPIRED' }),
              AuthDeviceActions.initialized({ user: null }),
            ];
          }
        }

        const user = tokenStorage.getUser();

        if (user) {
          return of(AuthDeviceActions.initialized({ user }));
        }

        return of(AuthDeviceActions.initialized({ user: null }));
      }),
    );
  },
  { functional: true },
);

export const init$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(ROOT_EFFECTS_INIT),
      map(() => AuthDeviceActions.init()),
    );
  },
  { functional: true },
);
